import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  CaretDownIcon,
  FadersHorizontalIcon,
  FloppyDiskIcon,
  PlusIcon,
  ShieldCheckeredIcon,
  TableIcon,
} from '@phosphor-icons/react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ApiEndpoint,
  FormActionType,
  FormType,
  TableActionType,
  Translation,
  VariantType,
  WafSchemaType,
} from '@enums';
import { FormHandlers, useFormList, useFormSelectedList } from '@hooks';
import { cn, validateValueEqual } from '@utils';

import { Text } from '@components/Text';
import { Button } from './Button';

interface ActionConfig {
  icon?: React.ReactNode;
  color: any;
  tooltip: string;
  condition: (data: any) => boolean;
}

type ActionConfigType = {
  [K in TableActionType]?: ActionConfig;
};

const actionConfigs: ActionConfigType = {
  [TableActionType.Create]: {
    icon: <PlusIcon weight="bold" className="size-4" />,
    color: 'blue',
    tooltip: 'Create',
    condition: () => true,
  },
  [TableActionType.Save]: {
    icon: <FloppyDiskIcon weight="bold" className="size-4" />,
    color: 'blue',
    tooltip: 'Create',
    condition: () => true,
  },
  [TableActionType.Verification]: {
    icon: <ShieldCheckeredIcon weight="fill" className="size-4" />,
    color: 'blue',
    tooltip: 'verify',
    condition: () => true,
  },
  [TableActionType.Priority]: {
    icon: <TableIcon weight="bold" className="size-4" />,
    color: 'blue',
    tooltip: 'verify',
    condition: () => true,
  },
  [TableActionType.Batch]: {
    icon: <CaretDownIcon className="size-4" />,
    color: 'blue',
    tooltip: 'batch',
    condition: () => true,
  },
  [TableActionType.Filter]: {
    icon: <FadersHorizontalIcon weight="bold" className="size-4" />,
    color: 'blue',
    tooltip: 'filter',
    condition: () => true,
  },
};

interface ActionButtonGroupProps<T> {
  actionHandlers: FormHandlers<T>;
  loading?: boolean;
  procedure?: FormActionType;
  defaultStatus?: string;
}

export const ActionButtonGroup = <T,>({
  actionHandlers,
  procedure = FormActionType.Idle,
  loading = false,
}: ActionButtonGroupProps<T>) => {
  const selectedIds = useFormSelectedList({ formType: FormType.CdnWAF, endpoint: ApiEndpoint.RULE });
  const formList = useFormList({ formType: FormType.CdnWAF, endpoint: ApiEndpoint.RULE });
  const selectedItems = formList.filter((item) => selectedIds.includes(item.id));
  const selectedStatus = selectedItems.map((item) => item.rule_status);
  const isSameStatus = selectedStatus.every((status) => status === selectedStatus[0]);

  const isSelectionEmpty = selectedIds.length === 0;

  const options = useFormList({
    formType: FormType.CdnWAF,
    endpoint: ApiEndpoint.SCHEMA,
  });
  const rule_status_options = options[WafSchemaType.RuleStatus] ?? [];

  const { t: tForms } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.waf' });
  const renderActionButtons = useMemo(() => {
    return Object.entries(actionHandlers)
      .map(([type, action], index) => {
        const actionType = type as keyof ActionConfigType;
        const config = actionConfigs[actionType];

        if (!config || !action.display) return null;

        const isDisabled = action.disabled || loading;
        const currentFormSubmitting = validateValueEqual(procedure, type);
        const isCleanButton = validateValueEqual(action.buttonType, VariantType.Clean);

        const handleClick = (value) => () => {
          action.handler(value);
        };

        if (validateValueEqual(type, FormActionType.Batch)) {
          return (
            <Menu key={index}>
              <MenuButton
                disabled={isSelectionEmpty}
                loading={action.loading && currentFormSubmitting}
                withoutScale
                className="rounded-lg"
                direction="right"
                adornment={<CaretDownIcon weight="bold" className="size-3" />}
                as={Button}
              >
                <Text $level={5} $customizeColor>
                  {tForms('ruleSets.action')}
                </Text>
              </MenuButton>
              <MenuItems transition anchor="bottom end" className="bg-white shadow-card z-20 rounded-lg mt-1 w-24">
                {rule_status_options.map((option, index) => {
                  const isStatusDisabled = isSameStatus && option.value === selectedStatus[0];

                  return (
                    <MenuItem
                      onClick={handleClick(option.value)}
                      withoutScale={isStatusDisabled}
                      disabled={isStatusDisabled}
                      className={cn(
                        'w-full rounded-lg text-left text-black',
                        'bg-transparent enabled:shadow-none',
                        !isStatusDisabled && 'hover:bg-slate-100 hover:text-gray-900',
                        isStatusDisabled && 'cursor-not-allowed text-theme-text-disabled',
                      )}
                      key={index}
                      as={Button}
                    >
                      {option.label}
                    </MenuItem>
                  );
                })}
              </MenuItems>
            </Menu>
          );
        }

        return (
          <Button
            key={type}
            onClick={() => action.handler()}
            disabled={isDisabled}
            variant={action.buttonType}
            loading={action.loading && currentFormSubmitting}
            predefinedColor={!isCleanButton ? (isDisabled ? 'zinc' : config.color) : undefined}
            adornment={config.icon}
            className={cn('rounded-lg')}
          >
            <Text $level={5} $customizeColor className="ellipsis-text">
              {action?.label ?? config.tooltip}
            </Text>
          </Button>
        );
      })
      .filter(Boolean);
  }, [actionHandlers, loading]);

  if (!actionHandlers || Object.keys(actionHandlers).length === 0) {
    return null;
  }

  return <div className="flex items-center space-x-5">{renderActionButtons}</div>;
};
